import { Check as CheckIcon } from 'lucide-react';
import { formatMoney } from '@/lib/money';
import { formatReceiptDateTime } from '@/lib/datetime';
import { useDocumentTitle } from '@/lib/useDocumentTitle';
import { useTranslation } from '@/i18n';
import { FeedbackPrompt } from '@/features/feedback/FeedbackPrompt';
import { TaxLine } from '@/features/check/TaxLine';
import { ReceiptDelivery } from './ReceiptDelivery';
import { TabletDone } from './TabletDone';
import { receiptShortCode } from '@/api/receipts';
import { addressLines } from './locationContact';
import type { Check, Location, Receipt } from '@/api/types';

interface ReceiptScreenProps {
    location: Location;
    check: Check;
    receipt: Receipt;
    accessToken: string;
}

/**
 * What the guest sees once the money has moved.
 *
 * The card below deliberately mirrors `RestaurantReceipt.html` in TouchTab.Receipt — the same six sections in
 * the same order, the same dashed rules, the same solid rule above the total, the same card-brand chip. A
 * guest gets one of these on screen and another by email, and two documents describing one transaction should
 * not look like they came from different companies.
 *
 * Two things sit outside that mirroring, because a screen is not a piece of paper. The confirmation above the
 * receipt exists because a guest's first question is "did it work", and a receipt answers that only by
 * implication. And the whole thing is built from brand tokens rather than the template's fixed greys, so it
 * re-skins per restaurant and survives dark mode.
 */
export function ReceiptScreen({ location, check, receipt, accessToken }: ReceiptScreenProps) {
    const { t, language } = useTranslation();
    const { currency, locale, timeZoneId } = location;
    const money = (minorUnits: number) => formatMoney(minorUnits, currency, locale);
    const shortCode = receiptShortCode(receipt.receiptLink);

    // The restaurant's own details, composed for a phone screen rather than for an email paragraph.
    const address = addressLines(location);

    useDocumentTitle(t('document.titleAtLocation', { name: location.name }));

    // Guest's language, restaurant's clock - see the note in datetime.ts.
    const paidAt = formatReceiptDateTime(receipt.paidAt, language, timeZoneId);

    // Split by which side of the subtotal they belong on. Same rule, and same reason, as the check screen.
    const preSubtotalDiscounts = check.discounts.filter((discount) => discount.isPreSubtotal);
    const postSubtotalDiscounts = check.discounts.filter((discount) => !discount.isPreSubtotal);

    return (
        <div className="min-h-dvh bg-muted">
            <div className="mx-auto flex w-full max-w-[var(--shell-max-width)] flex-col gap-4 px-5 pt-9 pb-12">
                {/*
                  * The answer to the only question the guest currently has, before any detail. `role="status"`
                  * rather than `alert`: this is good news arriving on a screen they just navigated to, and a
                  * polite announcement is enough to interrupt nothing.
                  */}
                <div role="status" className="flex flex-col items-center gap-2 text-center">
                    <span className="flex size-14 items-center justify-center rounded-full bg-primary text-primary-foreground">
                        <CheckIcon className="size-7" strokeWidth={3} aria-hidden="true" />
                    </span>

                    <h1 className="text-[1.75rem] leading-tight font-semibold tracking-tight text-balance">
                        {t('receipt.paidTitle')}
                    </h1>
                    <p className="tabular text-lg font-medium text-muted-foreground">
                        {money(receipt.amountCents)}
                    </p>

                    {/* The heading and amount above read as two fragments; this is the whole sentence. */}
                    <span className="sr-only">
                        {t('receipt.paidAnnouncement', { amount: money(receipt.amountCents) })}
                    </span>
                </div>

                <section
                    aria-label={t('receipt.receiptLabel')}
                    className="rounded-xl border border-border bg-card px-6 py-7 shadow-xs"
                >
                    {/* 1. Brand, centred — the template leads with the logo rather than the name. */}
                    <header className="flex flex-col items-center gap-1 text-center">
                        {location.logoOnLightUrl ? (
                            <img
                                src={location.logoOnLightUrl}
                                alt={location.name}
                                className="h-12 w-auto max-w-[70%] object-contain"
                            />
                        ) : (
                            <h2 className="text-lg font-semibold tracking-tight">{location.name}</h2>
                        )}

                        {/*
                          * Where the restaurant is and how to reach it — the same facts the emailed receipt
                          * carries, and the reason a guest keeps a receipt at all beyond the amount: finding
                          * the place again, and knowing who to call about a charge they do not recognise.
                          *
                          * Plain text rather than a `tel:` link: a receipt is a record of what happened, and
                          * a link invites a tap that dials the restaurant, which is not what anyone reading a
                          * receipt is trying to do. Rendered only when there is something to show; a location
                          * part-way through setup gets the header it had before rather than an empty line.
                          */}
                        {address.length > 0 && (
                            <address className="text-xs leading-relaxed text-muted-foreground not-italic">
                                {address.map((line) => (
                                    <span key={line} className="block">
                                        {line}
                                    </span>
                                ))}
                            </address>
                        )}

                        {location.phoneNumber && (
                            <span className="text-xs leading-relaxed text-muted-foreground">
                                {location.phoneNumber}
                            </span>
                        )}
                    </header>

                    {/*
                      * 2. Order meta.
                      *
                      * Matches the stored receipt TouchTab.Receipt emails, which is the same document a guest
                      * may open days later — the two disagreeing on what is on a receipt is worse than either
                      * choice about what to include.
                      *
                      * The address now sits in the header above, where a printed receipt puts it.
                      */}
                    <dl className="tabular mt-5 flex flex-col gap-1 text-sm">
                        <div className="flex justify-between gap-3">
                            <dt className="text-muted-foreground">{t('receipt.checkNumber')}</dt>
                            <dd className="font-medium">{check.number}</dd>
                        </div>
                        {check.serverName && (
                            <div className="flex justify-between gap-3">
                                <dt className="text-muted-foreground">{t('receipt.server')}</dt>
                                <dd className="min-w-0 text-right font-medium">{check.serverName}</dd>
                            </div>
                        )}
                        {paidAt && (
                            <div className="flex justify-between gap-3">
                                <dt className="text-muted-foreground">{t('receipt.date')}</dt>
                                <dd className="text-right font-medium">{paidAt}</dd>
                            </div>
                        )}
                    </dl>

                    <Separator />

                    {/* 3. Items. Quantity is a plain bold column here, matching the paper receipt. */}
                    <ul className="flex flex-col gap-2.5" aria-label={t('check.itemsLabel')}>
                        {check.items.map((item) => (
                            <li key={item.id}>
                                <div className="flex justify-between gap-3 text-[0.9375rem]">
                                    <span className="flex min-w-0 gap-1">
                                        <span aria-hidden="true" className="tabular w-6 shrink-0 font-semibold">
                                            {item.quantity}
                                        </span>
                                        <span className="sr-only">
                                            {t('check.quantityLabel', { count: item.quantity })}
                                        </span>
                                        <span className="font-medium">{item.name}</span>
                                    </span>
                                    <span className="tabular shrink-0">{money(item.totalCents)}</span>
                                </div>

                                {item.modifiers.length > 0 && (
                                    <ul className="mt-0.5 flex flex-col gap-0.5">
                                        {item.modifiers.map((modifier) => (
                                            <li
                                                key={modifier.id}
                                                className="flex justify-between gap-3 pl-7 text-[0.8125rem] text-muted-foreground"
                                            >
                                                <span className="min-w-0">{modifier.name}</span>
                                                {modifier.totalCents !== 0 && (
                                                    <span className="tabular shrink-0">
                                                        {money(modifier.totalCents)}
                                                    </span>
                                                )}
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </li>
                        ))}
                    </ul>

                    <Separator />

                    {/* 4. Totals. The grand total takes a solid rule where everything else is dashed. */}
                    <dl className="tabular flex flex-col gap-1 text-[0.9375rem]">
                        {/*
                          * Above the subtotal, because it already has these taken off.
                          *
                          * A receipt that does not name the reward a guest redeemed is the one place they
                          * cannot go back and check.
                          */}
                        {preSubtotalDiscounts.map((discount) => (
                            <div key={discount.id} className="flex justify-between gap-3">
                                <dt className="min-w-0 text-muted-foreground">
                                    {discount.name ?? t('check.discount')}
                                </dt>
                                <dd className="shrink-0">{money(-discount.amountCents)}</dd>
                            </div>
                        ))}

                        <div className="flex justify-between gap-3">
                            <dt className="text-muted-foreground">{t('check.subtotal')}</dt>
                            <dd>{money(check.subtotalCents)}</dd>
                        </div>

                        {/* And below it, because the subtotal above does not have these taken off yet. */}
                        {postSubtotalDiscounts.map((discount) => (
                            <div key={discount.id} className="flex justify-between gap-3">
                                <dt className="min-w-0 text-muted-foreground">
                                    {discount.name ?? t('check.discount')}
                                </dt>
                                <dd className="shrink-0">{money(-discount.amountCents)}</dd>
                            </div>
                        ))}

                        {check.surcharges.map((surcharge) => (
                            <div key={surcharge.id} className="flex justify-between gap-3">
                                <dt className="min-w-0 text-muted-foreground">
                                    {surcharge.name ?? t('check.surcharge')}
                                </dt>
                                <dd className="shrink-0">{money(surcharge.amountCents)}</dd>
                            </div>
                        ))}

                        <TaxLine
                            taxCents={check.taxCents}
                            digitalSurchargeCents={check.digitalSurchargeCents}
                            money={money}
                        />
                        <div className="flex justify-between gap-3">
                            <dt className="text-muted-foreground">{t('tip.tipLine')}</dt>
                            <dd>{money(receipt.tipCents)}</dd>
                        </div>
                        <div className="mt-1.5 flex justify-between gap-3 border-t border-foreground pt-2.5 text-base font-bold">
                            <dt>{t('check.total')}</dt>
                            <dd>{money(receipt.amountCents)}</dd>
                        </div>
                    </dl>

                    {(receipt.cardBrand || receipt.lastFour || receipt.authorizationCode) && (
                        <>
                            <Separator />

                            {/*
                              * 5. How it was paid.
                              *
                              * The name goes on a <section>, not on the <dl>. A description list has no role
                              * that supports being named, so an `aria-label` there is free to be dropped —
                              * the same trap the quantity badge on the check screen fell into. A titled
                              * section is a region, which does.
                              */}
                            <section aria-label={t('receipt.paymentLabel')}>
                            <dl className="tabular flex flex-col gap-1.5 text-sm">
                                {receipt.lastFour && (
                                    <div className="flex items-center justify-between gap-3">
                                        <dt>
                                            {receipt.cardBrand && (
                                                <span className="rounded bg-accent px-1.5 py-0.5 text-[0.7rem] font-bold tracking-wider text-accent-foreground uppercase">
                                                    {receipt.cardBrand}
                                                </span>
                                            )}
                                        </dt>
                                        <dd>
                                            {/* Bullets are decoration; the spoken form carries the meaning. */}
                                            <span aria-hidden="true">&bull;&bull;&bull;&bull; {receipt.lastFour}</span>
                                            <span className="sr-only">
                                                {t('receipt.cardEnding', { lastFour: receipt.lastFour })}
                                            </span>
                                        </dd>
                                    </div>
                                )}

                                {receipt.authorizationCode && (
                                    <div className="flex justify-between gap-3">
                                        <dt className="text-muted-foreground">{t('receipt.approvalCode')}</dt>
                                        <dd className="font-medium">{receipt.authorizationCode}</dd>
                                    </div>
                                )}
                            </dl>
                            </section>
                        </>
                    )}

                    {/* 6. The sign-off, above a dashed rule like the emailed one. */}
                    <p className="mt-5 border-t border-dashed border-border pt-4 text-center font-semibold tracking-wide text-pretty">
                        {t('receipt.thankYou')}
                    </p>
                </section>

                {/*
                  * Whatever this restaurant does about feedback: our own questions in the app, a link to a
                  * vendor's survey, or nothing at all. Which of the three is the server's decision, not this
                  * screen's - see `FeedbackPrompt`.
                  */}
                {/*
                  * A copy to keep, by email or text.
                  *
                  * Only once the receipt step has run. Until then there is no stored receipt to send and no
                  * short code to send it by - which on a payment that was charged and did not finish may be a
                  * while, because recovery makes one. Offering a button that cannot work is worse than
                  * offering none.
                  */}
                {shortCode && <ReceiptDelivery shortCode={shortCode} />}

                <FeedbackPrompt accessToken={accessToken} />

                {/*
                  * Last, and only on the tablet. Everything above it is something the guest may still want to
                  * do, so this is the one control that means "I am finished with all of it" - see `TabletDone`
                  * for why the tablet needs to be told rather than working it out.
                  */}
                <TabletDone />
            </div>
        </div>
    );
}

/** The dashed rule the paper receipt uses between sections. */
function Separator() {
    return <hr className="my-5 border-t border-dashed border-border" />;
}
